import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  FileText,
  LayoutDashboard,
  Megaphone,
  RefreshCw,
  ShieldCheck,
  Users,
  BriefcaseBusiness,
} from 'lucide-react';
import AdviserAdminSection, { type AdviserAdminTab } from '../advisers/AdviserAdminSection';
import UserManagementSection from '../components/UserManagementSection';
import CompanyOversightSection from '../components/CompanyOversightSection';
import MetaLeadAdsSection from '../components/MetaLeadAdsSection';
import {
  adminWorkspaceApi,
  type AdminPerson,
  type AdminRelationships,
  type ImpersonationAudit,
} from './api';
import {
  companyPractice,
  directOwners,
  entityName,
  personCompanies,
  personPractices,
  practiceCompanies,
  type EntityReference,
} from './relationships';
import AdminEntityDetail from './AdminEntityDetail';
import AdminImpersonationDialog from './AdminImpersonationDialog';
import './AdminWorkspace.css';

const sections = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'advisers', label: 'Adviser firms', icon: BriefcaseBusiness },
  { id: 'users', label: 'People', icon: Users },
  { id: 'companies', label: 'Companies', icon: Building2 },
  { id: 'support', label: 'Support sessions', icon: ShieldCheck },
  { id: 'integrations', label: 'Integrations', icon: Megaphone },
];
const descriptions: Record<string, string> = {
  overview:
    'Review what needs attention and follow the connections between firms, people and companies.',
  advisers: 'Approve applications, review the first public profile and curate featured firms.',
  users: 'See each person’s direct company access and adviser practice memberships.',
  companies: 'Understand independent ownership, adviser delegation and private demo access.',
  support: 'Review attributed support sessions and the reason recorded for each one.',
  integrations: 'Manage connected marketing services and platform publishing.',
};
export default function AdminWorkspace() {
  const [params, setParams] = useSearchParams();
  const rawSection = params.get('section') || 'overview';
  const section = sections.some((item) => item.id === rawSection) ? rawSection : 'overview';
  const query = params.get('q') || '';
  const filter = params.get('filter') || 'all';
  const reviewTab: AdviserAdminTab = ['practices', 'profiles', 'featured'].includes(
    params.get('review') || ''
  )
    ? (params.get('review') as AdviserAdminTab)
    : 'practices';
  const [data, setData] = useState<AdminRelationships | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [controls, setControls] = useState<{
    kind: 'users' | 'companies' | 'meta';
    search: string;
  } | null>(null);
  const [support, setSupport] = useState<{
    ref: EntityReference;
    candidates: AdminPerson[];
  } | null>(null);
  const [history, setHistory] = useState<ImpersonationAudit[] | null>(null);
  const [historyError, setHistoryError] = useState('');
  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await adminWorkspaceApi.relationships());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load administration.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const loadHistory = useCallback(async () => {
    setHistoryError('');
    try {
      setHistory(await adminWorkspaceApi.impersonationHistory());
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : 'Unable to load support history.');
    }
  }, []);
  useEffect(() => {
    if (section === 'support') void loadHistory();
  }, [section, loadHistory]);
  useEffect(() => {
    if (params.get('meta_connect')) setControls({ kind: 'meta', search: '' });
  }, [params]);
  const update = (values: Record<string, string | null>) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      Object.entries(values).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      return next;
    });
  const select = (ref: EntityReference) => update({ entity: ref.scope, id: ref.id });
  const selectedScope = params.get('entity');
  const selected =
    ['user', 'company', 'practice'].includes(selectedScope || '') && params.get('id')
      ? ({ scope: selectedScope, id: params.get('id') } as EntityReference)
      : null;
  const review = (practiceId: string, tab: 'practices' | 'profiles') =>
    update({
      section: 'advisers',
      review: tab,
      focus: practiceId,
      entity: null,
      id: null,
      q: null,
    });
  const searching = query.trim().toLowerCase();
  const matches = (...values: Array<string | null | undefined>) =>
    values.join(' ').toLowerCase().includes(searching);
  const companies =
    data?.companies.filter(
      (company) =>
        matches(
          company.name,
          company.industry,
          directOwners(data, company.id)
            .map((person) => person.name + ' ' + person.email)
            .join(' '),
          companyPractice(data, company.id)?.name
        ) &&
        (filter === 'all' ||
          (filter === 'demo'
            ? company.isDemo
            : filter === 'adviser'
              ? !!companyPractice(data, company.id)
              : !company.isDemo && !companyPractice(data, company.id)))
    ) || [];
  const people =
    data?.users.filter(
      (person) =>
        matches(
          person.name,
          person.email,
          personCompanies(data, person.id)
            .map((company) => company.name)
            .join(' '),
          personPractices(data, person.id)
            .map((practice) => practice.name)
            .join(' ')
        ) &&
        (filter === 'all' ||
          (filter === 'superuser' ? person.superUser : personPractices(data, person.id).length > 0))
    ) || [];
  const practices =
    data?.practices.filter(
      (practice) =>
        matches(
          practice.name,
          data.users.find((person) => person.id === practice.ownerUserId)?.email,
          practiceCompanies(data, practice.id)
            .map((company) => company.name)
            .join(' ')
        ) &&
        (filter === 'all' || practice.status === filter)
    ) || [];
  const directoryTab = params.get('review') === 'directory';
  const heading = sections.find((item) => item.id === section)?.label || 'Overview';
  const searchToolbar = (label: string, count: number, options: Array<[string, string]>) => (
    <div className="admin-toolbar">
      <label>
        {label}
        <input
          type="search"
          value={query}
          onChange={(event) => update({ q: event.target.value })}
          placeholder="Search names, email or connected records"
        />
      </label>
      <label>
        Show
        <select value={filter} onChange={(event) => update({ filter: event.target.value })}>
          {options.map(([value, name]) => (
            <option key={value} value={value}>
              {name}
            </option>
          ))}
        </select>
      </label>
      <span>
        {count} result{count === 1 ? '' : 's'}
      </span>
    </div>
  );
  const avatar = (photo: string | null, icon: 'user' | 'company' | 'practice') => (
    <span className="admin-avatar" aria-hidden="true">
      {photo ? (
        <img src={photo} alt="" />
      ) : icon === 'user' ? (
        <Users size={19} />
      ) : icon === 'company' ? (
        <Building2 size={20} />
      ) : (
        <BriefcaseBusiness size={20} />
      )}
    </span>
  );
  return (
    <div className="admin-shell adviser-workspace">
      <aside className="admin-sidebar">
        <Link to="/dashboard">
          <ArrowLeft size={15} />
          Back to workspace
        </Link>
        <p className="adviser-eyebrow">Platform administration</p>
        <nav aria-label="Administration">
          {sections.map((item) => (
            <Link
              key={item.id}
              to={`?section=${item.id}`}
              aria-current={section === item.id ? 'page' : undefined}
            >
              <item.icon size={17} />
              {item.label}
            </Link>
          ))}
          <Link to="/admin/blog">
            <FileText size={17} />
            Blog publishing
          </Link>
        </nav>
        <p className="admin-sidebar-note">
          Superuser access <br />
          Support sessions use each person’s actual permissions.
        </p>
      </aside>
      <main className="admin-main">
        <div className="admin-page-heading">
          <div>
            <p className="adviser-eyebrow">Onsavii administration</p>
            <h1>{heading}</h1>
            <p>{descriptions[section]}</p>
          </div>
          <button
            className="adviser-button adviser-button-secondary"
            type="button"
            disabled={loading}
            onClick={() => {
              void refresh();
              if (section === 'support') void loadHistory();
            }}
          >
            <RefreshCw size={15} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {error && (
          <p className="adviser-error" role="alert">
            {error}{' '}
            <button type="button" className="adviser-text-link" onClick={() => void refresh()}>
              Try again
            </button>
          </p>
        )}
        {!data ? (
          <p className="adviser-empty" role="status">
            {loading ? 'Loading connected accounts…' : 'Account information is unavailable.'}
          </p>
        ) : (
          <>
            {section === 'overview' && (
              <>
                <div className="admin-overview-counts">
                  <div>
                    <strong>{data.overview.users}</strong>
                    <span>People</span>
                  </div>
                  <div>
                    <strong>{data.overview.companies}</strong>
                    <span>Companies</span>
                  </div>
                  <div>
                    <strong>{data.overview.approvedPractices}</strong>
                    <span>Approved adviser firms</span>
                  </div>
                  <div>
                    <strong>{data.overview.demoCompanies}</strong>
                    <span>Private demos</span>
                  </div>
                </div>
                <h2>Needs attention</h2>
                <div className="admin-review-grid">
                  <article>
                    <strong>{data.overview.pendingApplications}</strong>
                    <h3>Adviser applications</h3>
                    <p>
                      Review the firm, its owner and application context before granting adviser
                      access.
                    </p>
                    <Link className="adviser-text-link" to="?section=advisers&review=practices">
                      Review applications <ArrowRight size={15} />
                    </Link>
                  </article>
                  <article>
                    <strong>
                      {
                        data.practices.filter(
                          (practice) => practice.profile?.reviewStage === 'submitted'
                        ).length
                      }
                    </strong>
                    <h3>First profile approvals</h3>
                    <p>
                      Approve the first firm profile. Owners then control their own publication and
                      updates.
                    </p>
                    <Link className="adviser-text-link" to="?section=advisers&review=profiles">
                      Review public profiles <ArrowRight size={15} />
                    </Link>
                  </article>
                </div>
                <div className="admin-section-heading">
                  <div>
                    <h2>Recent adviser relationships</h2>
                    <p>Client consent and company ownership are recorded separately.</p>
                  </div>
                  <Link className="adviser-text-link" to="?section=companies&filter=adviser">
                    All connected companies
                  </Link>
                </div>
                <div className="admin-connection-list">
                  {data.clientLinks
                    .slice()
                    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                    .slice(0, 6)
                    .map((link) => (
                      <div className="admin-connection-row" key={link.id}>
                        <div>
                          <button
                            type="button"
                            className="admin-related-button"
                            onClick={() => select({ scope: 'practice', id: link.practiceId })}
                          >
                            {entityName(data, { scope: 'practice', id: link.practiceId })}
                          </button>
                          <ArrowRight size={14} />
                          <button
                            type="button"
                            className="admin-related-button"
                            onClick={() => select({ scope: 'company', id: link.companyId })}
                          >
                            {entityName(data, { scope: 'company', id: link.companyId })}
                          </button>
                        </div>
                        <small>{link.status.replace(/_/g, ' ')}</small>
                      </div>
                    ))}
                  {!data.clientLinks.length && (
                    <p className="adviser-empty">No client delegation requests yet.</p>
                  )}
                </div>
              </>
            )}
            {section === 'advisers' && (
              <>
                <nav
                  className="adviser-admin-tabs admin-adviser-nav"
                  aria-label="Adviser administration"
                >
                  {[
                    ['directory', 'Firm directory'],
                    ['practices', 'Applications'],
                    ['profiles', 'Profile approval'],
                    ['featured', 'Homepage curation'],
                  ].map(([value, label]) => (
                    <Link
                      key={value}
                      to={`?section=advisers&review=${value}`}
                      aria-current={
                        (directoryTab ? value === 'directory' : reviewTab === value)
                          ? 'page'
                          : undefined
                      }
                    >
                      {label}
                    </Link>
                  ))}
                </nav>
                {directoryTab ? (
                  <>
                    {searchToolbar('Find an adviser firm', practices.length, [
                      ['all', 'All firms'],
                      ['pending', 'Pending'],
                      ['approved', 'Approved'],
                      ['suspended', 'Suspended'],
                      ['rejected', 'Rejected'],
                    ])}
                    <div className="admin-record-list">
                      {practices.map((practice) => (
                        <article className="admin-record" key={practice.id}>
                          <div className="admin-record-identity">
                            {avatar(null, 'practice')}
                            <div>
                              <h3>{practice.name}</h3>
                              <p>
                                {data.users.find((person) => person.id === practice.ownerUserId)
                                  ?.email || 'Owner account unavailable'}
                              </p>
                              <p>
                                {practiceCompanies(data, practice.id).length} client companies and
                                demos ·{' '}
                                {
                                  data.practiceMembers.filter(
                                    (member) => member.practiceId === practice.id
                                  ).length
                                }{' '}
                                team members
                              </p>
                              <div className="admin-tags">
                                <span>{practice.status}</span>
                                <span>
                                  {practice.profile?.publiclyListed
                                    ? 'Public profile'
                                    : 'Private profile'}
                                </span>
                                {practice.profile?.publiclyListed &&
                                  practice.profile.featuredOrder !== null && <span>Featured</span>}
                              </div>
                            </div>
                          </div>
                          <div className="adviser-actions">
                            <button
                              className="adviser-button adviser-button-secondary"
                              type="button"
                              onClick={() => select({ scope: 'practice', id: practice.id })}
                            >
                              View connections
                            </button>
                            <button
                              className="adviser-text-link"
                              type="button"
                              onClick={() => review(practice.id, 'practices')}
                            >
                              Review application
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="admin-toolbar">
                      <label>
                        Find a firm
                        <input
                          type="search"
                          value={query}
                          onChange={(event) => update({ q: event.target.value })}
                          placeholder="Firm name or application email"
                        />
                      </label>
                      {params.get('focus') && (
                        <button
                          type="button"
                          className="adviser-button adviser-button-secondary"
                          onClick={() => update({ focus: null })}
                        >
                          Show all firms
                        </button>
                      )}
                    </div>
                    <AdviserAdminSection
                      isVisible
                      inline
                      onClose={() => {}}
                      activeTab={reviewTab}
                      onTabChange={(tab) => update({ review: tab, focus: null })}
                      search={query}
                      focusPracticeId={params.get('focus') || undefined}
                      onChanged={() => void refresh()}
                      onPracticeDetails={(id) => select({ scope: 'practice', id })}
                    />
                  </>
                )}
              </>
            )}
            {section === 'users' && (
              <>
                {searchToolbar('Find a person', people.length, [
                  ['all', 'Everyone'],
                  ['adviser', 'Adviser team'],
                  ['superuser', 'Superusers'],
                ])}
                <div className="admin-record-list">
                  {people.map((person) => (
                    <article className="admin-record" key={person.id}>
                      <div className="admin-record-identity">
                        {avatar(person.photo, 'user')}
                        <div>
                          <h3>{person.name}</h3>
                          <p>{person.email}</p>
                          <p>
                            {personCompanies(data, person.id).length} companies ·{' '}
                            {personPractices(data, person.id)
                              .map((practice) => practice.name)
                              .join(', ') || 'No adviser practice'}
                          </p>
                          <div className="admin-tags">
                            {person.superUser && <span>Superuser</span>}
                            {person.isFounder && (
                              <span>
                                Personal Founder ·{' '}
                                {person.founderSubscriptionStatus || 'Status unavailable'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        className="adviser-button adviser-button-secondary"
                        type="button"
                        onClick={() => select({ scope: 'user', id: person.id })}
                      >
                        View connections
                      </button>
                    </article>
                  ))}
                </div>
                {!people.length && <p className="adviser-empty">No people match this search.</p>}
              </>
            )}
            {section === 'companies' && (
              <>
                {searchToolbar('Find a company', companies.length, [
                  ['all', 'All companies'],
                  ['adviser', 'Adviser connected'],
                  ['demo', 'Private demos'],
                  ['direct', 'Direct companies'],
                ])}
                <div className="admin-record-list">
                  {companies.map((company) => (
                    <article className="admin-record" key={company.id}>
                      <div className="admin-record-identity">
                        {avatar(company.logo, 'company')}
                        <div>
                          <h3>{company.name}</h3>
                          <p>
                            {company.industry || 'Industry not specified'} ·{' '}
                            {company.size || 'Size not specified'}
                          </p>
                          <p>
                            {company.isDemo
                              ? 'Practice-owned demo'
                              : company.provisionalPracticeId
                                ? 'Client ownership invitation pending'
                                : `Owner: ${
                                    directOwners(data, company.id)
                                      .map((person) => person.name)
                                      .join(', ') || 'No direct owner'
                                  }`}
                            {companyPractice(data, company.id)
                              ? ` · ${companyPractice(data, company.id)?.name}`
                              : ''}
                          </p>
                          <div className="admin-tags">
                            <span>
                              {company.isDemo
                                ? 'Private demo · no paid subscription'
                                : company.subscriptionTier}
                            </span>
                            {!company.isDemo && (
                              <span>{company.billingStatus || 'Billing status unavailable'}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        className="adviser-button adviser-button-secondary"
                        type="button"
                        onClick={() => select({ scope: 'company', id: company.id })}
                      >
                        View connections
                      </button>
                    </article>
                  ))}
                </div>
                {!companies.length && (
                  <p className="adviser-empty">No companies match this search.</p>
                )}
              </>
            )}
            {section === 'support' && (
              <>
                {historyError && (
                  <p role="alert" className="adviser-error">
                    {historyError}{' '}
                    <button type="button" onClick={() => void loadHistory()}>
                      Try again
                    </button>
                  </p>
                )}
                <p>
                  Open a person, firm or company record to start a support session. Every session
                  records the administrator, target account and reason.
                </p>
                <div className="admin-history">
                  {history?.map((item) => (
                    <article key={item.id}>
                      <div className="admin-section-heading">
                        <strong>
                          {entityName(data, { scope: 'user', id: item.actorUserId })} →{' '}
                          {entityName(data, { scope: 'user', id: item.targetUserId })}
                        </strong>
                        <span className="adviser-status">{item.status}</span>
                      </div>
                      <p>{item.reason}</p>
                      <small>
                        {item.scope === 'practice' ? 'Adviser firm' : item.scope} ·{' '}
                        {new Date(item.startedAt).toLocaleString()}
                        {item.endedAt ? ` · ended ${new Date(item.endedAt).toLocaleString()}` : ''}
                      </small>
                      {item.error && <p className="adviser-error">{item.error}</p>}
                    </article>
                  ))}
                  {history?.length === 0 && (
                    <p className="adviser-empty">No support sessions have been recorded.</p>
                  )}
                  {!history && !historyError && (
                    <p className="adviser-empty">Loading session history…</p>
                  )}
                </div>
              </>
            )}
            {section === 'integrations' && (
              <div className="admin-record-list">
                <article className="admin-record">
                  <div>
                    <h3>Meta Lead Ads</h3>
                    <p>Manage connected Facebook pages, webhook configuration and page access.</p>
                  </div>
                  <button
                    type="button"
                    className="adviser-button adviser-button-secondary"
                    onClick={() => setControls({ kind: 'meta', search: '' })}
                  >
                    Manage Meta Lead Ads
                  </button>
                </article>
                <article className="admin-record">
                  <div>
                    <h3>Blog publishing</h3>
                    <p>Create, edit and publish platform articles.</p>
                  </div>
                  <Link className="adviser-button adviser-button-secondary" to="/admin/blog">
                    Open blog management
                  </Link>
                </article>
              </div>
            )}
          </>
        )}
      </main>
      {data && selected && !support && !controls && (
        <AdminEntityDetail
          data={data}
          selected={selected}
          onSelect={select}
          onClose={() => update({ entity: null, id: null })}
          onImpersonate={(ref, candidates) => setSupport({ ref, candidates })}
          onManage={(kind, search) => setControls({ kind, search })}
          onReview={review}
        />
      )}
      {data && support && (
        <AdminImpersonationDialog
          key={`${support.ref.scope}:${support.ref.id}`}
          scope={support.ref.scope}
          scopeId={support.ref.id}
          name={entityName(data, support.ref)}
          candidates={support.candidates}
          onClose={() => setSupport(null)}
        />
      )}
      <UserManagementSection
        isVisible={controls?.kind === 'users'}
        initialSearch={controls?.search}
        onClose={() => {
          setControls(null);
          void refresh();
        }}
      />
      <CompanyOversightSection
        isVisible={controls?.kind === 'companies'}
        initialSearch={controls?.search}
        onClose={() => {
          setControls(null);
          void refresh();
        }}
      />
      <MetaLeadAdsSection
        isVisible={controls?.kind === 'meta'}
        onClose={() => setControls(null)}
        searchParams={params}
        setSearchParams={setParams}
      />
    </div>
  );
}
